import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import AiAnalysis from "../../../utils/AiAnalysis.jsx";
import ApiService from "../../../services/ApiService";
import Markdown from 'react-markdown';
import { Spinner } from 'react-bootstrap';

export default function ButtonAi({ postItem = {}, isMobile = false, className = '' }) {
    const [show, setShow] = useState(false);
    const [post, setPost] = useState({});
    const [loading, setLoading] = useState(false);
    const [aiResult, setAiResult] = useState();
    const [lastAnalyzedId, setLastAnalyzedId] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [userScrolledUp, setUserScrolledUp] = useState(false);
    const [showScrollDownButton, setShowScrollDownButton] = useState(false);
    const contentRef = useRef(null);
    const modalBodyRef = useRef(null);
    const throttleTimeoutRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const lastScrollTop = useRef(0);
    const isAutoScrolling = useRef(false);
    const userScrolledUpRef = useRef(false);

    const handleClose = () => {
        // Cleanup timeouts when closing
        if (throttleTimeoutRef.current) {
            clearTimeout(throttleTimeoutRef.current);
        }
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        setShow(false);
    };

    // Check if user is near bottom of scroll
    const isNearBottom = useCallback(() => {
        if (!contentRef.current) return false;
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        return scrollHeight - scrollTop - clientHeight < 100; // 100px threshold
    }, []);

    // Manual scroll to bottom (no longer auto-triggered during streaming)
    const scrollToBottom = useCallback(() => {
        // Find the correct scrollable element
        let scrollElement = null;
        const modalBody = modalBodyRef.current;
        const aiContainer = contentRef.current;
        
        if (modalBody && modalBody.scrollHeight > modalBody.clientHeight) {
            scrollElement = modalBody;
        } else if (aiContainer && aiContainer.scrollHeight > aiContainer.clientHeight) {
            scrollElement = aiContainer;
        }
        
        if (scrollElement) {
            isAutoScrolling.current = true;
            scrollElement.scrollTo({
                top: scrollElement.scrollHeight,
                behavior: 'smooth'
            });
            
            // Reset auto scrolling flag after animation
            setTimeout(() => {
                isAutoScrolling.current = false;
            }, 500);
        }
    }, []);

    // Manual scroll to bottom when user clicks button
    const handleScrollToBottom = useCallback(() => {
        // Find the correct scrollable element
        let scrollElement = null;
        const modalBody = modalBodyRef.current;
        const aiContainer = contentRef.current;
        
        if (modalBody && modalBody.scrollHeight > modalBody.clientHeight) {
            scrollElement = modalBody;
        } else if (aiContainer && aiContainer.scrollHeight > aiContainer.clientHeight) {
            scrollElement = aiContainer;
        }
        
        if (scrollElement) {
            isAutoScrolling.current = true;
            scrollElement.scrollTo({
                top: scrollElement.scrollHeight,
                behavior: 'smooth'
            });
            setUserScrolledUp(false);
            userScrolledUpRef.current = false;
            setShowScrollDownButton(false);
            
            setTimeout(() => {
                isAutoScrolling.current = false;
            }, 500);
        }
    }, []);

    // Update ref when state changes
    useEffect(() => {
        userScrolledUpRef.current = userScrolledUp;
    }, [userScrolledUp]);

    // Setup scroll listener
    useEffect(() => {
        // Add a small delay to ensure the modal and content are fully rendered
        const setupListener = () => {
            // Try to find the scrollable element - could be modal body or ai-container
            const modalBody = modalBodyRef.current;
            const aiContainer = contentRef.current;
            
            let scrollElement = null;
            
            // Check which element is actually scrollable
            if (modalBody && modalBody.scrollHeight > modalBody.clientHeight) {
                scrollElement = modalBody;
            } else if (aiContainer && aiContainer.scrollHeight > aiContainer.clientHeight) {
                scrollElement = aiContainer;
            }
            
            if (!scrollElement || !show) {
                return null;
            }

            const handleScrollEvent = () => {
                if (isAutoScrolling.current) {
                    return;
                }
                
                const currentScrollTop = scrollElement.scrollTop;
                const { scrollTop, scrollHeight, clientHeight } = scrollElement;
                const scrollDirection = currentScrollTop > lastScrollTop.current ? 'down' : 'up';
                const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
                
                lastScrollTop.current = currentScrollTop;

                // Clear any existing timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // Show scroll down button if user is not at bottom and there's content to scroll
                if (!nearBottom) {
                    setShowScrollDownButton(true);
                } else {
                    // If user is near bottom, hide scroll down button  
                    setShowScrollDownButton(false);
                }
                
                // Keep track of user scroll state for other purposes
                if (scrollDirection === 'up' && !nearBottom) {
                    setUserScrolledUp(true);
                    userScrolledUpRef.current = true;
                } else if (nearBottom) {
                    setUserScrolledUp(false);
                    userScrolledUpRef.current = false;
                }

                // Set timeout to detect when user stops scrolling
                scrollTimeoutRef.current = setTimeout(() => {
                    const finalNearBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight < 100;
                    if (finalNearBottom) {
                        setUserScrolledUp(false);
                        userScrolledUpRef.current = false;
                        setShowScrollDownButton(false);
                    }
                }, 1000);
            };

            scrollElement.addEventListener('scroll', handleScrollEvent, { passive: true });
            
            return () => {
                scrollElement.removeEventListener('scroll', handleScrollEvent);
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            };
        };

        if (show) {
            // Add a small delay to ensure elements are rendered
            const timer = setTimeout(setupListener, 100);
            return () => {
                clearTimeout(timer);
                // Call setupListener directly to get the cleanup function
                const cleanup = setupListener();
                if (cleanup) cleanup();
            };
        }
    }, [show]); // Only depend on show state

    // Reset scroll state when modal opens/closes
    useEffect(() => {
        if (show) {
            setUserScrolledUp(false);
            userScrolledUpRef.current = false;
            setShowScrollDownButton(false);
            lastScrollTop.current = 0;
        }
    }, [show]);

    // Cleanup timeouts on component unmount
    useEffect(() => {
        return () => {
            if (throttleTimeoutRef.current) {
                clearTimeout(throttleTimeoutRef.current);
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Throttled update function for smoother streaming
    const handleStreamUpdate = useCallback((content) => {
        // Clear previous timeout
        if (throttleTimeoutRef.current) {
            clearTimeout(throttleTimeoutRef.current);
        }

        // Throttle updates to avoid too frequent re-renders
        throttleTimeoutRef.current = setTimeout(() => {
            setAiResult(content);
            // Auto scroll after a short delay to ensure content is rendered
            // setTimeout(scrollToBottom, 50); // Disabled auto-scroll when streaming
        }, 100); // Update every 100ms instead of every chunk
    }, []); // Removed scrollToBottom dependency

    const handleRecommend = async () => {
        try {
            setShow(true);
            // Nếu postId khác với lần phân tích trước hoặc chưa có kết quả phân tích
            if (lastAnalyzedId !== postItem._id || !aiResult) {
                setLoading(true);
                setIsStreaming(true);
                setAiResult(''); // Reset content for new analysis
                const result = await ApiService.get("/posts/" + postItem._id);
                if (result._id) {
                    setPost(result);                
                    const tempDiv = document.createElement('article');
                    const titleH1 = `<h1>${result.title}</h1>`;
                    const sapoH2 = `<h2>${result.description}</h2>`;
                    const titleGoogle = result.title_google;
                    const descriptionGoogle = result.description_google;
                    const tags = result.tags.map(tag => tag.label).join(', ') || '';
                
                    tempDiv.innerHTML = result.content || '';
                    const contentResult = tempDiv.innerHTML;
                    
                    const dataAi = JSON.stringify({
                        title: titleH1,
                        description: sapoH2, 
                        titleGoogle: titleGoogle,
                        descriptionGoogle: descriptionGoogle,
                        content: contentResult,
                        tags: tags
                    });

                    const response = await AiAnalysis(dataAi, 'phan-tich-bai-viet', handleStreamUpdate);
                    setLastAnalyzedId(postItem._id);
                    setLoading(false);
                    setIsStreaming(false);                
                }
            }
        } catch (error) {
            console.error('Error in handleRecommend:', error);
            setLoading(false);
            setIsStreaming(false);
            handleClose();
        }
    }

    const handleReRecommend = async () => {
        try {
            setLoading(true);
            setIsStreaming(true);
            setAiResult(''); // Reset content for re-analysis
            const tempDiv = document.createElement('article');
            const titleH1 = `<h1>${post.title}</h1>`;
            const sapoH2 = `<h2>${post.description}</h2>`;
            const titleGoogle = post.title_google;
            const descriptionGoogle = post.description_google;
            const tags = post.tags.map(tag => tag.label).join(', ') || '';
        
            tempDiv.innerHTML = post.content || '';
            const contentResult = tempDiv.innerHTML;
            
            const dataAi = JSON.stringify({
                title: titleH1,
                description: sapoH2,
                titleGoogle: titleGoogle,
                descriptionGoogle: descriptionGoogle,
                content: contentResult,
                tags: tags
            });

            const response = await AiAnalysis(dataAi, 'phan-tich-bai-viet', handleStreamUpdate);
            setLoading(false);
            setIsStreaming(false);
        } catch (error) {
            console.error('Error in handleReRecommend:', error);
            setLoading(false);
            setIsStreaming(false);
        }
    }

    return (
        <>
            <button className={`btn btn-ai-recommend ${className}`} onClick={handleRecommend}>
            {!isMobile ? <>
                <span className="mr-2">
                    <svg fillRule="evenodd" viewBox="64 64 896 896" focusable="false" dataIcon="open-a-i" width="16px" height="16px" fill="currentColor" ariaHidden="true">
                        <path d="M482.88 128c-84.35 0-156.58 52.8-185.68 126.98-60.89 8.13-115.3 43.63-146.6 97.84-42.16 73-32.55 161.88 17.14 224.16-23.38 56.75-19.85 121.6 11.42 175.78 42.18 73.02 124.1 109.15 202.94 97.23C419.58 898.63 477.51 928 540.12 928c84.35 0 156.58-52.8 185.68-126.98 60.89-8.13 115.3-43.62 146.6-97.84 42.16-73 32.55-161.88-17.14-224.16 23.38-56.75 19.85-121.6-11.42-175.78-42.18-73.02-124.1-109.15-202.94-97.23C603.42 157.38 545.49 128 482.88 128m0 61.54c35.6 0 68.97 13.99 94.22 37.74-1.93 1.03-3.92 1.84-5.83 2.94l-136.68 78.91a46.11 46.11 0 00-23.09 39.78l-.84 183.6-65.72-38.34V327.4c0-76 61.9-137.86 137.94-137.86m197.7 75.9c44.19 3.14 86.16 27.44 109.92 68.57 17.8 30.8 22.38 66.7 14.43 100.42-1.88-1.17-3.6-2.49-5.53-3.6l-136.73-78.91a46.23 46.23 0 00-46-.06l-159.47 91.1.36-76.02 144.5-83.41a137.19 137.19 0 0178.53-18.09m-396.92 55.4c-.07 2.2-.3 4.35-.3 6.56v157.75a46.19 46.19 0 0022.91 39.9l158.68 92.5-66.02 37.67-144.55-83.35c-65.86-38-88.47-122.53-50.45-188.34 17.78-30.78 46.55-52.69 79.73-62.68m340.4 79.93l144.54 83.35c65.86 38 88.47 122.53 50.45 188.34-17.78 30.78-46.55-52.69-79.73-62.68.07-2.19.3-4.34.3-6.55V570.85a46.19-6.19 0 00-22.9-39.9l-158.69-92.5zM511.8 464.84l54.54 31.79-.3 63.22-54.84 31.31-54.54-31.85.3-63.16zm100.54 58.65l65.72 38.35V728.6c0 76-61.9 137.86-137.94 137.86-35.6 0-68.97-13.99-94.22-37.74 1.93-1.03 3.92-1.84 5.83-2.94l136.68-78.9a46.11 46.11 0 0023.09-39.8zm-46.54 89.55l-.36 76.02-144.5 83.41c-65.85 38-150.42 15.34-188.44-50.48-17.8-30.8-22.38-66.7-14.43-100.42 1.88 1.17 3.6 2.5 5.53 3.6l136.74 78.91a46.23 46.23 0 0046 .06z"></path>
                    </svg>
                </span> Phân tích AI
            </> : 'AI'}
            </button>
            <Modal 
                show={show} 
                onHide={handleClose}
                dialogClassName="modal-600w h-full"
                className="ai-recommend-modal"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>AI phân tích</Modal.Title>
                </Modal.Header>
                <Modal.Body className="ai-recommend-body" ref={modalBodyRef}>
                    {loading && !aiResult ? (
                        <div className="ai-loading-container">
                            <div className="ai-icon-container">
                                <svg fillRule="evenodd" viewBox="64 64 896 896" focusable="false" dataIcon="open-a-i" width="32px" height="32px" fill="#6366f1" ariaHidden="true" className="rotating-icon">
                                    <path d="M482.88 128c-84.35 0-156.58 52.8-185.68 126.98-60.89 8.13-115.3 43.63-146.6 97.84-42.16 73-32.55 161.88 17.14 224.16-23.38 56.75-19.85 121.6 11.42 175.78 42.18 73.02 124.1 109.15 202.94 97.23C419.58 898.63 477.51 928 540.12 928c84.35 0 156.58-52.8 185.68-126.98 60.89-8.13 115.3-43.62 146.6-97.84 42.16-73 32.55-161.88-17.14-224.16 23.38-56.75 19.85-121.6-11.42-175.78-42.18-73.02-124.1-109.15-202.94-97.23C603.42 157.38 545.49 128 482.88 128m0 61.54c35.6 0 68.97 13.99 94.22 37.74-1.93 1.03-3.92 1.84-5.83 2.94l-136.68 78.91a46.11 46.11 0 00-23.09 39.78l-.84 183.6-65.72-38.34V327.4c0-76 61.9-137.86 137.94-137.86m197.7 75.9c44.19 3.14 86.16 27.44 109.92 68.57 17.8 30.8 22.38 66.7 14.43 100.42-1.88-1.17-3.6-2.49-5.53-3.6l-136.73-78.91a46.23 46.23 0 00-46-.06l-159.47 91.1.36-76.02 144.5-83.41a137.19 137.19 0 0178.53-18.09m-396.92 55.4c-.07 2.2-.3 4.35-.3 6.56v157.75a46.19 46.19 0 0022.91 39.9l158.68 92.5-66.02 37.67-144.55-83.35c-65.86-38-88.47-122.53-50.45-188.34 17.78-30.78 46.55-52.69 79.73-62.68m340.4 79.93l144.54 83.35c65.86 38 88.47 122.53 50.45 188.34-17.78 30.78-46.55-52.69-79.73-62.68.07-2.19.3-4.34.3-6.55V570.85a46.19 46.19 0 00-22.9-39.9l-158.69-92.5zM511.8 464.84l54.54 31.79-.3 63.22-54.84 31.31-54.54-31.85.3-63.16zm100.54 58.65l65.72 38.35V728.6c0 76-61.9 137.86-137.94 137.86-35.6 0-68.97-13.99-94.22-37.74 1.93-1.03 3.92-1.84 5.83-2.94l136.68-78.9a46.11 46.11 0 0023.09-39.8zm-46.54 89.55l-.36 76.02-144.5 83.41c-65.85 38-150.42 15.34-188.44-50.48-17.8-30.8-22.38-66.7-14.43-100.42 1.88 1.17 3.6 2.5 5.53 3.6l136.74 78.91a46.23 46.23 0 0046 .06z"></path>
                                </svg>
                                <div className="pulse-ring">                                   
                                </div>                                
                            </div>
                            <div className="loading-text">
                                <div className="d-flex align-items-center">AI đang phân tích  
                                    <div className="loading-dots" style={{ marginLeft: '10px' }}>
                                        <span>•</span>
                                        <span>•</span>
                                        <span>•</span>
                                    </div>
                                </div>
                            </div>                            
                        </div>
                    ) : (
                        <>
                            <div 
                                className="ai-container" 
                                ref={contentRef}
                                style={{
                                    height: isMobile ? '100%' : '100%', // Subtract header + footer + buttons height
                                    overflowY: 'auto',
                                    transition: 'all 0.3s ease',
                                    scrollBehavior: 'smooth',
                                    position: 'relative'
                                }}
                            >
                                <div style={{ 
                                    opacity: aiResult ? 1 : 0.5,
                                    transition: 'opacity 0.3s ease',
                                    minHeight: '20px',
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: isMobile ? '1.4' : '1.6',
                                    padding: isMobile ? '8px' : '12px'
                                }}>
                                    <Markdown>{aiResult || (isStreaming ? 'Đang khởi tạo phân tích...' : '')}</Markdown>
                                </div>
                                {isStreaming && (
                                    <div className="streaming-indicator mt-3" style={{
                                        opacity: 0.7,
                                        transform: 'translateY(0)',
                                        transition: 'all 0.3s ease',
                                        animation: 'fadeInUp 0.5s ease',
                                        padding: isMobile ? '8px' : '12px'
                                    }}>
                                        <div className="d-flex align-items-center text-muted">
                                            <div className="streaming-pulse" style={{
                                                width: isMobile ? '6px' : '8px',
                                                height: isMobile ? '6px' : '8px',
                                                borderRadius: '50%',
                                                backgroundColor: '#6366f1',
                                                marginRight: '8px',
                                                animation: 'pulse 1.5s infinite'
                                            }}></div>
                                            <small style={{ fontSize: isMobile ? '0.75em' : '0.85em' }}>Đang nhận phản hồi từ AI</small>
                                            <div className="loading-dots" style={{ marginLeft: '8px', fontSize: isMobile ? '10px' : '12px' }}>
                                                <span style={{ animationDelay: '0s' }}>•</span>
                                                <span style={{ animationDelay: '0.2s' }}>•</span>
                                                <span style={{ animationDelay: '0.4s' }}>•</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={`d-flex justify-content-center ${isMobile ? 'mt-3' : 'mt-4'}`} style={{
                                position: 'absolute',
                                bottom: '16px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                            }}>
                                <button 
                                    className={`btn btn-ai-recommend ${isMobile ? 'btn-sm' : ''}`}
                                    onClick={handleReRecommend}
                                    disabled={isStreaming}
                                    style={{
                                        opacity: isStreaming ? 0.6 : 1,
                                        transform: isStreaming ? 'scale(0.98)' : 'scale(1)',
                                        transition: 'all 0.2s ease',
                                        fontSize: isMobile ? '14px' : '16px',
                                        padding: isMobile ? '6px 12px' : '8px 16px'
                                    }}
                                >
                                    {isStreaming ? (
                                        <>
                                            <span className="mr-2">
                                                <div style={{
                                                    width: isMobile ? '10px' : '12px',
                                                    height: isMobile ? '10px' : '12px',
                                                    border: '2px solid #ffffff',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite',
                                                    display: 'inline-block'
                                                }}></div>
                                            </span>
                                            Đang phân tích...
                                        </>
                                    ) : 'Phân tích lại'}
                                </button>
                            </div>
                            <style jsx>{`
                                @keyframes fadeInUp {
                                    from {
                                        opacity: 0;
                                        transform: translateY(10px);
                                    }
                                    to {
                                        opacity: 0.7;
                                        transform: translateY(0);
                                    }
                                }
                                
                                @keyframes slideInRight {
                                    from {
                                        opacity: 0;
                                        transform: translateX(20px);
                                    }
                                    to {
                                        opacity: 1;
                                        transform: translateX(0);
                                    }
                                }
                                
                                @keyframes pulse {
                                    0%, 100% {
                                        opacity: 1;
                                        transform: scale(1);
                                    }
                                    50% {
                                        opacity: 0.5;
                                        transform: scale(1.1);
                                    }
                                }
                                
                                @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                                
                                .loading-dots span {
                                    animation: bounce 1.4s infinite ease-in-out both;
                                }
                                
                                @keyframes bounce {
                                    0%, 80%, 100% {
                                        transform: scale(0);
                                        opacity: 0.5;
                                    }
                                    40% {
                                        transform: scale(1);
                                        opacity: 1;
                                    }
                                }
                                
                                .ai-container::-webkit-scrollbar {
                                    width: ${isMobile ? '2px' : '4px'};
                                }
                                
                                .ai-container::-webkit-scrollbar-track {
                                    background: #f1f1f1;
                                    border-radius: 2px;
                                }
                                
                                .ai-container::-webkit-scrollbar-thumb {
                                    background: #c1c1c1;
                                    border-radius: 2px;
                                }
                                
                                .ai-container::-webkit-scrollbar-thumb:hover {
                                    background: #a8a8a8;
                                }

                                @media (max-width: 768px) {
                                    .ai-recommend-modal .modal-dialog {
                                        margin: 10px;
                                        max-width: calc(100vw - 20px);
                                    }
                                    
                                    .ai-recommend-modal .modal-content {
                                        border-radius: 8px;
                                    }
                                    
                                    .ai-recommend-modal .modal-header {
                                        padding: 12px 16px;
                                    }
                                    
                                    .ai-recommend-modal .modal-body {
                                        padding: 12px 16px;
                                    }
                                    
                                    .ai-recommend-modal .modal-footer {
                                        padding: 12px 16px;
                                    }
                                }
                            `}</style>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Đóng
                    </Button>
                    <Button variant="primary" onClick={handleClose}>
                        Xác nhận
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}